import immutableStore from 'utils/immutableStore';
import {List, Record, Set, Map} from 'immutable';
import alt from 'components/Dispatcher';
import DirectReportsActions from 'actions/coaching/DirectReportsActions';
import {Store} from '@store/@types/alt';
import UserActivityRanks from 'records/UserActivityRanks';
import Team from 'records/Team';
import User, {UserId} from 'records/User';
import {UserSkillBreakdown} from 'records/UserSkillBreakdown';
import {CoachingPlanVelocity} from 'records/CoachingPlanVelocity';
import {associateBy} from 'utils/associateBy';
import {PlanId} from 'records/CoachingPlan';

class DirectReportsStoreState extends Record({
  teams: List<Team>(),
  directReports: List<User>(),
  directReportsLoading: false,
  directReportsError: false,
  activityRankings: Set<UserActivityRanks>(),
  activityRankingsLoading: false,
  activityRankingsError: false,
  skillBreakdowns: Map<UserId, UserSkillBreakdown>(),
  skillBreakdownsLoading: false,
  skillBreakdownsError: false,
  planVelocities: List<CoachingPlanVelocity>(),
  velocityByPlanId: Map<PlanId, CoachingPlanVelocity>(),
  planVelocitiesLoading: false,
  planVelocitiesError: false,
}) {
  readonly teams: List<Team>;
  readonly directReports: List<User>;
  readonly directReportsLoading: boolean;
  readonly directReportsError: boolean;
  readonly activityRankings: Set<UserActivityRanks>;
  readonly activityRankingsLoading: boolean;
  readonly activityRankingsError: boolean;
  readonly skillBreakdowns: Map<UserId, UserSkillBreakdown>;
  readonly skillBreakdownsLoading: boolean;
  readonly skillBreakdownsError: boolean;
  readonly planVelocities: List<CoachingPlanVelocity>;
  readonly velocityByPlanId: Map<PlanId, CoachingPlanVelocity>;
  readonly planVelocitiesLoading: boolean;
  readonly planVelocitiesError: boolean;
}

/**
 * This is an alt.js store to support metrics for managers and execs in Coaches Corner.
 *
 * We have a few main collections we're concerned about here -
 *   - Direct reports: These are user details - names, avatars, etc
 *   - Plan velocities: Stats on how the plans are progressing - progress since reviews, etc
 */
@immutableStore
class DirectReportsStore extends Store<DirectReportsStoreState> {
  constructor() {
    super();
    this.state = new DirectReportsStoreState();
    this.bindActions(DirectReportsActions);
  }

  directReports() {
    this.setState({
      teams: List<Team>(),
      directReports: List<User>(),
      directReportsLoading: true,
      directReportsError: false,
    });
  }

  directReportsSuccess(teams) {
    this.setState({
      teams: teams,
      directReports: teams.flatMap(team => team.users),
      directReportsLoading: false,
      directReportsError: false,
    });
  }

  directReportsError() {
    this.setState({
      teams: List(),
      directReports: List(),
      directReportsLoading: false,
      directReportsError: true,
    });
  }

  getActivityRankings() {
    this.setState({
      activityRankingsLoading: true,
      activityRankingsError: false,
    });
  }

  getActivityRankingsSuccess(activityRankings) {
    this.setState({
      activityRankings,
      activityRankingsLoading: false,
    });
  }

  getActivityRankingsError() {
    this.setState({
      activityRankingsLoading: false,
      activityRankingsError: true,
    });
  }

  getSkillBreakdown() {
    this.setState({
      skillBreakdownsLoading: true,
      skillBreakdownsError: false,
    });
  }

  getSkillBreakdownSuccess(skillBreakdowns: UserSkillBreakdown[]) {
    this.setState({
      skillBreakdowns: associateBy(skillBreakdowns, s => s.userId, this.state.skillBreakdowns),
      skillBreakdownsLoading: false,
      skillBreakdownsError: false,
    });
  }

  getSkillBreakdownError() {
    this.setState({
      skillBreakdownsLoading: false,
      skillBreakdownsError: true,
    });
  }

  planVelocitiesSuccess(planVelocities) {
    this.setState({
      planVelocities: planVelocities,
      velocityByPlanId: associateBy(planVelocities, p => p.planId),
      planVelocitiesLoading: false,
    });
  }

  planVelocitiesError() {
    this.setState({
      planVelocitiesError: true,
      planVelocitiesLoading: false,
    });
  }
}

export default alt.createStore(DirectReportsStore, 'DirectReportsStore');
